import React, { useCallback, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

function useTabStepper(tabs, activeTab, onTabPress) {
  const activeIndex = activeTab ? tabs.indexOf(activeTab) : -1;
  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < tabs.length - 1 || activeIndex === -1;

  const goPrev = useCallback(() => {
    if (activeIndex <= 0) return;
    onTabPress?.(tabs[activeIndex - 1]);
  }, [activeIndex, onTabPress, tabs]);

  const goNext = useCallback(() => {
    if (!tabs.length) return;
    if (activeIndex < 0) {
      onTabPress?.(tabs[0]);
      return;
    }
    if (activeIndex >= tabs.length - 1) return;
    onTabPress?.(tabs[activeIndex + 1]);
  }, [activeIndex, onTabPress, tabs]);

  return { activeIndex, canGoPrev, canGoNext, goPrev, goNext };
}

/** Horizontal scroll strip matching the main calendar tab bar. */
export function CalendarSidebarTabNav({
  tabs = [],
  activeTab,
  onTabPress,
  theme,
  compact = false,
}) {
  const scrollRef = useRef(null);
  const tabOffsetsRef = useRef({});
  const styles = createScrollStyles(theme, compact);
  const isDark = theme.background?.primary !== '#ffffff';

  useEffect(() => {
    if (!activeTab || !scrollRef.current) return;
    const x = tabOffsetsRef.current[activeTab];
    if (typeof x !== 'number') return;
    scrollRef.current.scrollTo({ x: Math.max(0, x - 12), animated: true });
  }, [activeTab]);

  return (
    <View style={styles.wrap}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
      >
        {tabs.map((tabKey) => {
          const isActive = tabKey === activeTab;
          return (
            <TouchableOpacity
              key={`sidebar-tab-${tabKey}`}
              style={[styles.tabBtn, isActive && styles.tabBtnActive]}
              onPress={() => onTabPress?.(tabKey)}
              onLayout={(e) => {
                tabOffsetsRef.current[tabKey] = e.nativeEvent.layout.x;
              }}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
            >
              <Text style={[styles.tabText, isActive && styles.tabTextActive]} numberOfLines={1}>
                {tabKey}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

/** Single day navigation chevron. */
export function CalendarDayChevron({ direction, onPress, theme, size = 'md' }) {
  const styles = createChevronStyles(theme, size);
  const accent = theme.brand.accent;
  const iconSize = size === 'sm' ? 15 : 17;
  const isPrev = direction === 'prev';

  return (
    <TouchableOpacity
      style={styles.btn}
      onPress={onPress}
      accessibilityLabel={isPrev ? 'Previous day' : 'Next day'}
    >
      <Ionicons name={isPrev ? 'chevron-back' : 'chevron-forward'} size={iconSize} color={accent} />
    </TouchableOpacity>
  );
}

/** Compact prev / next chevrons for stepping through calendar days. */
export function CalendarDayChevrons({ onPrevDay, onNextDay, theme, size = 'md' }) {
  const styles = createChevronStyles(theme, size);

  return (
    <View style={styles.row}>
      <CalendarDayChevron direction="prev" onPress={onPrevDay} theme={theme} size={size} />
      <CalendarDayChevron direction="next" onPress={onNextDay} theme={theme} size={size} />
    </View>
  );
}

/** Compact prev / next chevrons for stepping through calendar tabs. */
export function CalendarTabChevrons({
  tabs = [],
  activeTab,
  onTabPress,
  theme,
  size = 'md',
}) {
  const { canGoPrev, canGoNext, goPrev, goNext } = useTabStepper(tabs, activeTab, onTabPress);
  const styles = createChevronStyles(theme, size);
  const accent = theme.brand.accent;
  const textSecondary = theme.text.secondary;
  const iconSize = size === 'sm' ? 15 : 17;

  return (
    <View style={styles.row}>
      <TouchableOpacity
        style={[styles.btn, !canGoPrev && styles.btnDisabled]}
        onPress={goPrev}
        disabled={!canGoPrev}
        accessibilityLabel="Previous section"
      >
        <Ionicons name="chevron-back" size={iconSize} color={canGoPrev ? accent : textSecondary} />
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.btn, !canGoNext && styles.btnDisabled]}
        onPress={goNext}
        disabled={!canGoNext}
        accessibilityLabel="Next section"
      >
        <Ionicons name="chevron-forward" size={iconSize} color={canGoNext ? accent : textSecondary} />
      </TouchableOpacity>
    </View>
  );
}

function createScrollStyles(theme, compact) {
  const isDark = theme.background?.primary !== '#ffffff';
  return StyleSheet.create({
    wrap: {
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
      backgroundColor: theme.background.elevated,
    },
    scroll: {
      flexGrow: 0,
    },
    scrollContent: {
      alignItems: 'center',
      gap: compact ? 4 : 6,
      paddingHorizontal: compact ? 10 : 12,
      paddingVertical: compact ? 6 : 8,
    },
    tabBtn: {
      paddingHorizontal: compact ? 6 : 8,
      paddingVertical: compact ? 4 : 5,
      borderRadius: 6,
    },
    tabBtnActive: {
      backgroundColor: `${theme.brand.accent}18`,
    },
    tabText: {
      fontSize: compact ? 10 : 11,
      fontWeight: '800',
      color: isDark ? '#3ff5a7' : 'rgb(59, 163, 120)',
      letterSpacing: 0.3,
    },
    tabTextActive: {
      color: '#7c6cdd',
      textDecorationLine: 'underline',
    },
  });
}

function createChevronStyles(theme, size) {
  const dim = size === 'sm' ? 30 : 34;
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
    },
    btn: {
      width: dim,
      height: dim,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.card,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    btnDisabled: {
      opacity: 0.45,
    },
  });
}

export default CalendarSidebarTabNav;
